import { Dimensions } from "react-native";

const { width: screenWidth, height: screenHeight } = Dimensions.get("window");

// Types
export interface MediaViewerProps {
  visible: boolean;
  onClose: () => void;
  media: {
    uri: string;
    name?: string;
    mimeType?: string;
  };
}

export interface ValidationResult {
  isValid: boolean;
  error: string | null;
}

// MIME Type Detection Functions
export const isImage = (mimeType?: string): boolean => {
  return mimeType?.includes("image") || false;
};

export const isVideo = (mimeType?: string): boolean => {
  // Check for video MIME types including MP4
  const videoTypes = [
    "video/",
    "mp4",
    "avi",
    "mov",
    "wmv",
    "flv",
    "webm",
    "mkv",
  ];
  return (
    videoTypes.some((type) => mimeType?.toLowerCase().includes(type)) || false
  );
};

export const isAudio = (mimeType?: string): boolean => {
  return mimeType?.includes("audio") || false;
};

// Validation Functions
export const validateVideoSource = (
  uri: string,
  mimeType?: string,
): ValidationResult => {
  // Check if URI is valid
  if (!uri || uri.trim() === "") {
    return { isValid: false, error: "No video source provided" };
  }

  // Check if it's a valid URL or data URI
  try {
    new URL(uri);
  } catch {
    // If it's not a valid URL, check if it's a data URI
    if (!uri.startsWith("data:")) {
      return { isValid: false, error: "Invalid video source URL" };
    }
  }

  return { isValid: true, error: null };
};

export const validateWebVideoSource = (
  uri: string,
  mimeType?: string,
): ValidationResult => {
  // Check if URI is valid
  if (!uri || uri.trim() === "") {
    return { isValid: false, error: "No video source provided" };
  }

  // Check if it's a valid URL or data URI
  try {
    new URL(uri);
  } catch {
    // If it's not a valid URL, check if it's a data URI
    if (!uri.startsWith("data:")) {
      return { isValid: false, error: "Invalid video source URL" };
    }
  }

  // Check MIME type compatibility for web
  if (mimeType) {
    const supportedTypes = [
      "video/mp4",
      "video/webm",
      "video/ogg",
      "video/avi",
      "video/mov",
      "video/*",
      "video/wmv",
    ];

    if (
      !supportedTypes.some((type) =>
        mimeType.toLowerCase().includes(type.toLowerCase()),
      )
    ) {
      return { isValid: false, error: `Unsupported video format: ${mimeType}` };
    }
  }

  return { isValid: true, error: null };
};

// Debugging Functions
export const logMediaInfo = (
  media: MediaViewerProps["media"],
  platform: string,
) => {
  console.log(`MediaViewer.${platform} - Media info:`, {
    uri: media.uri,
    mimeType: media.mimeType,
    name: media.name,
    isVideo: isVideo(media.mimeType),
    isImage: isImage(media.mimeType),
    isAudio: isAudio(media.mimeType),
  });
};

// Error Handling Functions
export const getVideoErrorMessage = (error: MediaError | null): string => {
  if (!error) return "Failed to load video";

  switch (error.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return "Video loading was aborted";
    case MediaError.MEDIA_ERR_NETWORK:
      return "Network error while loading video";
    case MediaError.MEDIA_ERR_DECODE:
      return "Video decoding error";
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return "Video format not supported";
    default:
      return `Video error: ${error.message || "Unknown error"}`;
  }
};

export const logVideoError = (
  error: MediaError | null,
  uri: string,
  mimeType?: string,
) => {
  console.error("Video error details:", {
    error: error,
    src: uri,
    mimeType: mimeType,
    errorCode: error?.code,
    errorMessage: error?.message,
  });
};

// Constants
export const MEDIA_CONSTANTS = {
  SCREEN_WIDTH: screenWidth,
  SCREEN_HEIGHT: screenHeight,
  VIDEO_TYPES: ["video/", "mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"],
  WEB_SUPPORTED_VIDEO_TYPES: [
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/avi",
    "video/mov",
    "video/*",
    "video/wmv",
  ],
  TIMEOUT_DURATION: 5000, // 5 seconds
} as const;

// Style Constants
export const MEDIA_STYLES = {
  OVERLAY: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    justifyContent: "center" as const,
    alignItems: "center" as const,
  },
  OVERLAY_TOUCHABLE: {
    flex: 1,
    width: "100%",
    justifyContent: "center" as const,
    alignItems: "center" as const,
  },
  CONTAINER: {
    width: screenWidth * 0.95,
    height: screenHeight * 0.9,
    backgroundColor: "white",
    borderRadius: 12,
    overflow: "hidden" as const,
  },
  HEADER: {
    flexDirection: "row" as const,
    justifyContent: "space-between" as const,
    alignItems: "center" as const,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  TITLE: {
    flex: 1,
    fontSize: 16,
    fontWeight: "600" as const,
    marginRight: 16,
  },
  HEADER_ACTIONS: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
  },
  CONTENT: {
    flex: 1,
    justifyContent: "center" as const,
    alignItems: "center" as const,
    minHeight: 200,
  },
  IMAGE: {
    width: screenWidth * 0.9,
    height: screenHeight * 0.85,
    resizeMode: "contain" as const,
  },
  VIDEO: {
    width: screenWidth * 0.9,
    height: screenHeight * 0.5,
  },
  WEB_VIDEO: {
    width: screenWidth * 0.9,
    height: screenHeight * 0.5,
    backgroundColor: "#000",
  },
  WEB_AUDIO: {
    width: "100%",
    maxWidth: 400,
    marginBottom: 16,
  },
  HIDDEN_VIDEO: {
    width: 0,
    height: 0,
    opacity: 0,
  },
  AUDIO_CONTAINER: {
    alignItems: "center" as const,
    paddingVertical: 40,
  },
  AUDIO_TEXT: {
    fontSize: 16,
    fontWeight: "500" as const,
    marginTop: 16,
    textAlign: "center" as const,
  },
  UNKNOWN_CONTAINER: {
    alignItems: "center" as const,
    paddingVertical: 40,
  },
  UNKNOWN_TEXT: {
    fontSize: 16,
    fontWeight: "500" as const,
    marginTop: 16,
    textAlign: "center" as const,
  },
  UNKNOWN_SUBTEXT: {
    fontSize: 14,
    color: "#666",
    marginTop: 8,
    textAlign: "center" as const,
  },
  ERROR_CONTAINER: {
    alignItems: "center" as const,
    paddingVertical: 40,
  },
  ERROR_TEXT: {
    fontSize: 16,
    fontWeight: "500" as const,
    marginTop: 16,
    textAlign: "center" as const,
    color: "#FF6B6B",
  },
  ERROR_SUBTEXT: {
    fontSize: 14,
    color: "#666",
    marginTop: 8,
    textAlign: "center" as const,
  },
} as const;
